import React, { createContext, useContext, ReactNode, useState, useEffect } from "react";

export enum ClickState {
  UP_NOW,
  UP,
  DOWN_NOW,
  DOWN,
}

interface Pointer {
  clickState: ClickState;
  position: { x: number, y: number } | null;

}

const PointerContext = createContext<
  Pointer | undefined
>(undefined);

type PointerContextProviderProps = {
  children: ReactNode;
};

export const PointerProvider: React.FC<PointerContextProviderProps> = ({
  children,
}) => {

  const [clicked, setClicked] = useState(ClickState.UP)
  const [hovered, setHovered] = useState<{ x: number, y: number } | null>(null)

  const lastMoveEventRef = React.useRef<{ clientX: number; clientY: number; currentTarget: Element } | null>(null);

  function onHover() {
    if (!lastMoveEventRef.current || lastMoveEventRef.current.currentTarget === null) {
      return;
    }
    var rect = lastMoveEventRef.current.currentTarget.getBoundingClientRect();
    var x = lastMoveEventRef.current.clientX - rect.left;
    var y = lastMoveEventRef.current.clientY - rect.top;
    setHovered({ x, y })
    lastMoveEventRef.current = null;
  }

  // Handle touch events for mobile
  function handleTouchStart(e: React.TouchEvent) {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      const rect = e.currentTarget.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      setHovered({ x, y });
      setClicked(ClickState.DOWN_NOW);
    }
  }

  function handleTouchEnd(e: React.TouchEvent) {
    // Use changedTouches for touchend since touches array is empty
    if (e.changedTouches.length > 0) {
      const touch = e.changedTouches[0];
      const rect = e.currentTarget.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      setHovered({ x, y });
    }
    setClicked(ClickState.UP_NOW);
  }

  useEffect(() => {
    setInterval(() => {
      onHover();
    }, 50)
  }, [])

  useEffect(() => {
    if (clicked === ClickState.UP_NOW) {
      setClicked(ClickState.UP)
    } else if (clicked === ClickState.DOWN_NOW) {
      setClicked(ClickState.DOWN)
    }
  }, [clicked])

  return (
    <div
      style={{ width: "100%", height: "100%" }}
      onMouseDown={() => setClicked(ClickState.DOWN_NOW)}
      onMouseUp={() => setClicked(ClickState.UP_NOW)}
      onMouseMove={e => lastMoveEventRef.current = { clientX: e.clientX, clientY: e.clientY, currentTarget: e.currentTarget }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <PointerContext.Provider value={{ clickState: clicked, position: hovered }}>
        {children}
      </PointerContext.Provider>
    </div>
  );
};

export function useMousePosition(): Pointer {
  const context = useContext(PointerContext);
  if (!context) {
    throw new Error(
      "useBalloonEventsPointer must be used within a BalloonEventPointer"
    );
  }
  return context;
}
